// What a config file holds, judged the way the server judges it. Mirrors GameConfigContent on the backend,
// which is the authority — this copy is here so a picked file or a Publish dialog can say what is wrong
// before the round trip. Pure: imports nothing that needs a browser, so the rules can be unit-tested alone.
//
// Empty is content that delivers nothing: blank text, or for JSON a bare null, {} or []. It is a fine
// draft — a new file starts as {} — but it is never imported and never published.

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

export const isEmptyJson = (value) => value === null
    || (Array.isArray(value) && value.length === 0)
    || (isPlainObject(value) && Object.keys(value).length === 0);

// Whether `text` delivers nothing. Invalid JSON is not empty: it is a different problem, and a branch
// cannot hold it anyway, since every save parses its content first. A file in another format (stored
// before imports became JSON-only) is opaque text, so only blank is empty there.
export const isEmptyContent = (format, text) => {
    if (typeof text !== 'string' || !text.trim()) return true;
    if (String(format || '').toUpperCase() !== 'JSON') return false;
    try {
        return isEmptyJson(JSON.parse(text));
    } catch {
        return false;
    }
};

const isJsonSpace = (c) => c === ' ' || c === '\t' || c === '\n' || c === '\r';

// Valid JSON re-indented two spaces deep, the layout JSON.stringify(value, null, 2) prints, and anything
// else returned untouched. Only whitespace OUTSIDE strings moves: every token is copied as it was typed.
// That is the point of not going through JSON.parse — a round trip rounds an integer past 2^53, turns
// 1.0 into 1 and rewrites escapes, which would quietly change a config just by opening it.
export const reindentJson = (text) => {
    if (typeof text !== 'string' || !text.trim()) return text ?? '';
    try {
        JSON.parse(text);
    } catch {
        return text;
    }
    // Valid from here on, so every string is closed and every bracket matched.
    let out = '';
    let depth = 0;
    const newline = () => `\n${'  '.repeat(depth)}`;
    for (let i = 0; i < text.length; i += 1) {
        const c = text[i];
        if (c === '"') {
            let end = i + 1;
            while (text[end] !== '"') end += text[end] === '\\' ? 2 : 1;
            out += text.slice(i, end + 1);
            i = end;
        } else if (c === '{' || c === '[') {
            let next = i + 1;
            while (isJsonSpace(text[next])) next += 1;
            if (text[next] === (c === '{' ? '}' : ']')) {
                out += c + text[next]; // an empty container stays on one line, as stringify prints it
                i = next;
            } else {
                depth += 1;
                out += c + newline();
            }
        } else if (c === '}' || c === ']') {
            depth -= 1;
            out += newline() + c;
        } else if (c === ',') {
            out += `,${newline()}`;
        } else if (c === ':') {
            out += ': ';
        } else if (!isJsonSpace(c)) {
            out += c;
        }
    }
    return out;
};

// What the editor shows for a stored branch: JSON laid out to read, other formats exactly as stored.
// Content the server writes — a merge carried down from a parent, a live event's copy — is stored on
// one line, so without this a branch nobody typed in opens as a single wall of text.
export const displayContent = (format, content) => (
    String(format || '').toUpperCase() === 'JSON' ? reindentJson(content ?? '') : (content ?? '')
);

// The byte-order mark some Windows editors write at the top of a file. JSON.parse refuses it, and so
// does the server, so it is dropped as the file is read.
export const stripBom = (text) => (typeof text === 'string' && text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text);

// geo-play-web reads a JSON request body with Spring's default codec limit (it sets no
// `spring.codec.max-in-memory-size`), so a create carrying a bigger file comes back 413. Measured on the
// request as sent: escaping the text into a JSON string makes it larger than the file on disk.
export const MAX_CREATE_REQUEST_BYTES = 256 * 1024;
// Room for everything else in the create: name, format, area, origin and tags.
const REQUEST_OVERHEAD_BYTES = 2 * 1024;

export const createRequestBytes = (text) =>
    new TextEncoder().encode(JSON.stringify(String(text ?? ''))).length + REQUEST_OVERHEAD_BYTES;

const kb = (bytes) => `${Math.ceil(bytes / 1024)} KB`;

// Why a picked .json file cannot be imported, or null when it can. Worked out once, when it is picked.
export const contentProblem = (text) => {
    if (typeof text !== 'string' || !text.trim()) return 'The file is empty.';
    let value;
    try {
        value = JSON.parse(text);
    } catch (e) {
        return `Not valid JSON: ${e.message}`;
    }
    if (isEmptyJson(value)) return 'The file is empty.';
    const bytes = createRequestBytes(text);
    if (bytes > MAX_CREATE_REQUEST_BYTES) {
        return `Too large to import: ${kb(bytes)} once sent, and the server takes at most ${kb(MAX_CREATE_REQUEST_BYTES)}.`;
    }
    return null;
};
